/**
 * Platform Events Management Panel — Admin Dashboard
 *
 * Read-only list view with "Ended"/"Active" badges, row click to detail.
 * Event creation lives in the campaign channel-creation flow.
 *
 * API Endpoints:
 * - GET  /api/v1/admin/events        — List all events
 *
 * @story SCRUM-1470 — 0.1.1.10-02: Platform Events Management Panel
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import apiRequest from '../../services/api';

// ─── Styles ────────────────────────────────────────────────────────

const styles = {
  container: { maxWidth: '1200px', margin: '0 auto', padding: '24px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '600', margin: 0 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e5e7eb', fontWeight: '600', color: '#6b7280' },
  td: { padding: '10px 12px', borderBottom: '1px solid #f3f4f6' },
  rowPast: { opacity: 0.55 },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: '600' },
  badgeActive: { backgroundColor: '#dcfce7', color: '#166534' },
  badgeEnded: { backgroundColor: '#f3f4f6', color: '#6b7280' },
  badgeInactive: { backgroundColor: '#fee2e2', color: '#991b1b' },
  row: { cursor: 'pointer' },
};

// ─── Component ─────────────────────────────────────────────────────

export default function EventsPanel() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest('/api/v1/admin/events');
      if (res.success) {
        setEvents(res.data);
      }
    } catch {
      // Error handled by apiRequest
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const formatDate = (iso) => {
    if (!iso) { return '—'; }
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  };

  const getStatusBadge = (event) => {
    if (!event.active) {
      return <span style={{ ...styles.badge, ...styles.badgeInactive }}>Inactive</span>;
    }
    if (event.is_past) {
      return <span style={{ ...styles.badge, ...styles.badgeEnded }}>Ended</span>;
    }
    return <span style={{ ...styles.badge, ...styles.badgeActive }}>Active</span>;
  };

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="events"
      onNavigate={handleNavigate}
      user={{
        name: authUser?.fullName || authUser?.firstName || 'Admin',
        email: authUser?.email || '',
        avatar: authUser?.profilePicture || null
      }}
    >
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Platform Events</h1>
      </div>

      {loading ? (
        <p>Loading events...</p>
      ) : events.length === 0 ? (
        <p style={{ color: '#6b7280' }}>No events yet. Create one to get started.</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Market</th>
              <th style={styles.th}>Starts</th>
              <th style={styles.th}>Ends</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>HubSpot ID</th>
              <th style={styles.th}>Codes</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => (
              <tr
                key={ev.id}
                style={{ ...styles.row, ...(ev.is_past ? styles.rowPast : {}) }}
                onClick={() => navigate(`/admin/marketing/events/${ev.id}`)}
              >
                <td style={styles.td}>{ev.name}</td>
                <td style={styles.td}>{ev.market_name}</td>
                <td style={styles.td}>{formatDate(ev.starts_at)}</td>
                <td style={styles.td}>{formatDate(ev.ends_at)}</td>
                <td style={styles.td}>{getStatusBadge(ev)}</td>
                <td style={styles.td}><code style={{ fontSize: '12px' }}>{ev.hubspot_event_id}</code></td>
                <td style={styles.td}>{ev.intent_code_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
    </DashboardLayout>
  );
}
